/**
 * Validator.ts
 * Enforces 12 dungeon design regulations derived from ADRs.
 * Returns violations sorted by severity (error > warning > info).
 */

import type { GridCell, OctileCell, SquareConnector, GridPos, CellType } from '../types/OctileTypes.ts'
import { PASSABLE_WALLS } from '../types/OctileTypes.ts'
import { getCellType, getOctWallToward, getConnWallToward } from '../grid/GridAddressing.ts'
import { CELL_LIMITS } from '../types/ContentTypes.ts'

// ─── Types ──────────────────────────────────────────────────

export interface Violation {
    ruleId: string
    severity: 'error' | 'warning' | 'info'
    message: string
    cells: GridPos[]
}

const HAZARD_TYPES: readonly CellType[] = [
    'trap_floor', 'trap_walls', 'trap_ceiling', 'gauntlet', 'puzzle', 'mimic_den',
] as const

const SAFE_TYPES: readonly CellType[] = ['entrance', 'shrine', 'alcove'] as const

// ─── Validator ──────────────────────────────────────────────

export class Validator {
    /**
     * Run all 12 regulation checks against the grid.
     * Returns violations sorted: errors first, then warnings, then info.
     */
    validate(grid: GridCell[][], entrance: GridPos | null): Violation[] {
        const violations: Violation[] = []
        const h = grid.length
        const w = h > 0 ? grid[0].length : 0

        // Rule 1: entrance exists
        const entranceCell = this.findEntrance(grid, h, w)
        if (!entranceCell) {
            violations.push({
                ruleId: 'entrance-exists',
                severity: 'error',
                message: 'No entrance cell defined',
                cells: [],
            })
            // Many rules depend on entrance — return early
            return this.addRemainingChecks(violations, grid, h, w, null)
        }

        // Rule 2: full connectivity
        const reachable = this.bfs(grid, h, w, entranceCell)
        const unreachable = this.findUnreachable(grid, h, w, reachable)
        if (unreachable.length > 0) {
            violations.push({
                ruleId: 'full-connectivity',
                severity: 'error',
                message: `${unreachable.length} cell${unreachable.length > 1 ? 's are' : ' is'} unreachable from entrance`,
                cells: unreachable,
            })
        }

        // Rule 3: entrance safe zone
        const unsafeNear = this.checkEntranceSafeZone(grid, h, w, entranceCell)
        if (unsafeNear.length > 0) {
            violations.push({
                ruleId: 'entrance-safe-zone',
                severity: 'warning',
                message: `Enemies or traps within ${CELL_LIMITS.entranceSafeDistance} cell${CELL_LIMITS.entranceSafeDistance > 1 ? 's' : ''} of entrance`,
                cells: unsafeNear,
            })
        }

        return this.addRemainingChecks(violations, grid, h, w, entranceCell)
    }

    /** Rules 4–12 (don't require entrance for most, but pass it when available) */
    private addRemainingChecks(
        violations: Violation[], grid: GridCell[][],
        h: number, w: number, entrance: GridPos | null
    ): Violation[] {
        // Rule 4: shrine/alcove safe zones
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active || cell.kind !== 'octagon') continue
                const oct = cell as OctileCell
                if (oct.type === 'shrine' || oct.type === 'alcove') {
                    if (oct.spawns.length > 0 || oct.traps.length > 0) {
                        violations.push({
                            ruleId: 'shrine-safe-zones',
                            severity: 'warning',
                            message: `Safe zone violated — enemies/traps in ${oct.type} at [${r},${c}]`,
                            cells: [{ row: r, col: c }],
                        })
                    }
                }
            }
        }

        // Rule 5: trap count octagon
        // Rule 6: trap count connector
        // Rule 8: enemy density
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active) continue

                if (cell.kind === 'octagon') {
                    const oct = cell as OctileCell
                    if (oct.traps.length > CELL_LIMITS.maxTrapsPerOctagon) {
                        violations.push({
                            ruleId: 'trap-count-octagon',
                            severity: 'warning',
                            message: `Cell [${r},${c}] has ${oct.traps.length} traps (max ${CELL_LIMITS.maxTrapsPerOctagon})`,
                            cells: [{ row: r, col: c }],
                        })
                    }
                    if (oct.spawns.length > CELL_LIMITS.maxEnemiesPerOctagon) {
                        violations.push({
                            ruleId: 'enemy-density',
                            severity: 'warning',
                            message: `Cell [${r},${c}] has ${oct.spawns.length} enemies (max ${CELL_LIMITS.maxEnemiesPerOctagon})`,
                            cells: [{ row: r, col: c }],
                        })
                    }
                } else if (cell.kind === 'connector') {
                    const conn = cell as SquareConnector
                    if (conn.traps.length > CELL_LIMITS.maxTrapsPerConnector) {
                        violations.push({
                            ruleId: 'trap-count-connector',
                            severity: 'warning',
                            message: `Connector [${r},${c}] has ${conn.traps.length} traps (max ${CELL_LIMITS.maxTrapsPerConnector})`,
                            cells: [{ row: r, col: c }],
                        })
                    }
                }
            }
        }

        // Rule 7: consecutive trap cells
        const trapChains = this.findConsecutiveTrapCells(grid, h, w)
        if (trapChains.length > 0) {
            violations.push({
                ruleId: 'consecutive-traps',
                severity: 'warning',
                message: `${trapChains.length} cell${trapChains.length > 1 ? 's in' : ' in a'} hazard chain without rest area nearby`,
                cells: trapChains,
            })
        }

        // Rule 9: boss distance
        if (entrance) {
            const entranceDist = this.bfs(grid, h, w, entrance)
            for (let r = 0; r < h; r++) {
                for (let c = 0; c < w; c++) {
                    const cell = grid[r]?.[c]
                    if (!cell?.active || cell.kind !== 'octagon') continue
                    if ((cell as OctileCell).type === 'boss_throne') {
                        const key = `${r},${c}`
                        const dist = entranceDist.get(key) ?? Infinity
                        if (dist < CELL_LIMITS.minBossDistance) {
                            violations.push({
                                ruleId: 'boss-distance',
                                severity: 'warning',
                                message: `Boss at [${r},${c}] is only ${dist} steps from entrance (min ${CELL_LIMITS.minBossDistance})`,
                                cells: [{ row: r, col: c }],
                            })
                        }
                    }
                }
            }
        }

        // Rule 10: reward ratio
        let totalOctagons = 0
        let lootOctagons = 0
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active || cell.kind !== 'octagon') continue
                totalOctagons++
                if ((cell as OctileCell).loot.length > 0) lootOctagons++
            }
        }
        if (totalOctagons > 0) {
            const ratio = lootOctagons / totalOctagons
            if (ratio < CELL_LIMITS.minRewardRatio) {
                violations.push({
                    ruleId: 'reward-ratio',
                    severity: 'warning',
                    message: `Only ${Math.round(ratio * 100)}% of cells have loot (minimum ${Math.round(CELL_LIMITS.minRewardRatio * 100)}%)`,
                    cells: [],
                })
            }
        }

        // Rule 11: safe zone distance
        const safeCells = this.findCellsByType(grid, h, w, SAFE_TYPES)
        if (safeCells.length > 0) {
            // BFS from all safe cells simultaneously
            const safeReach = this.bfsMultiSource(grid, h, w, safeCells)
            const tooFar: GridPos[] = []
            for (let r = 0; r < h; r++) {
                for (let c = 0; c < w; c++) {
                    const cell = grid[r]?.[c]
                    if (!cell?.active) continue
                    const dist = safeReach.get(`${r},${c}`) ?? Infinity
                    if (dist > CELL_LIMITS.minSafeZoneDistance) {
                        tooFar.push({ row: r, col: c })
                    }
                }
            }
            if (tooFar.length > 0) {
                violations.push({
                    ruleId: 'safe-zone-distance',
                    severity: 'info',
                    message: `${tooFar.length} cell${tooFar.length > 1 ? 's are' : ' is'} more than ${CELL_LIMITS.minSafeZoneDistance} cells from nearest safe zone`,
                    cells: tooFar,
                })
            }
        } else if (totalOctagons > 0) {
            // No safe zones at all — every cell is too far
            violations.push({
                ruleId: 'safe-zone-distance',
                severity: 'info',
                message: 'No safe zones (shrine/alcove/entrance) found',
                cells: [],
            })
        }

        // Rule 12: gate prerequisites
        const gateViolations = this.checkGatePrerequisites(grid, h, w, entrance)
        violations.push(...gateViolations)

        // Sort: error > warning > info
        const order = { error: 0, warning: 1, info: 2 }
        violations.sort((a, b) => order[a.severity] - order[b.severity])

        return violations
    }

    // ─── BFS ────────────────────────────────────────────────

    /** BFS from a single source. Returns map of "row,col" → distance. */
    private bfs(
        grid: GridCell[][], h: number, w: number,
        start: GridPos
    ): Map<string, number> {
        return this.bfsMultiSource(grid, h, w, [start])
    }

    /** BFS from multiple sources simultaneously. */
    private bfsMultiSource(
        grid: GridCell[][], h: number, w: number,
        sources: GridPos[]
    ): Map<string, number> {
        const dist = new Map<string, number>()
        const queue: GridPos[] = []

        for (const s of sources) {
            const key = `${s.row},${s.col}`
            if (!dist.has(key)) {
                dist.set(key, 0)
                queue.push(s)
            }
        }

        let head = 0
        while (head < queue.length) {
            const cur = queue[head++]
            const curDist = dist.get(`${cur.row},${cur.col}`)!
            const neighbors = this.getPassableNeighbors(grid, h, w, cur.row, cur.col)

            for (const n of neighbors) {
                const nKey = `${n.row},${n.col}`
                if (!dist.has(nKey)) {
                    dist.set(nKey, curDist + 1)
                    queue.push(n)
                }
            }
        }

        return dist
    }

    /** BFS that avoids crossing specific gate walls. Returns reachable keys. */
    private bfsAvoidingGate(
        grid: GridCell[][], h: number, w: number,
        start: GridPos, blockedGates: Set<string>
    ): Set<string> {
        const visited = new Set<string>()
        const queue: GridPos[] = [start]
        visited.add(`${start.row},${start.col}`)

        let head = 0
        while (head < queue.length) {
            const cur = queue[head++]
            const neighbors = this.getPassableNeighbors(grid, h, w, cur.row, cur.col, blockedGates)

            for (const n of neighbors) {
                const nKey = `${n.row},${n.col}`
                if (!visited.has(nKey)) {
                    visited.add(nKey)
                    queue.push(n)
                }
            }
        }

        return visited
    }

    // ─── Neighbor Helpers ───────────────────────────────────

    /**
     * Get passable neighbors of a cell. Checks wall states on BOTH sides.
     * Optional blockedGates set: "fromR,fromC>toR,toC" edges to treat as impassable.
     */
    private getPassableNeighbors(
        grid: GridCell[][], h: number, w: number,
        row: number, col: number,
        blockedGates?: Set<string>
    ): GridPos[] {
        const cell = grid[row]?.[col]
        if (!cell?.active) return []

        const result: GridPos[] = []
        const kind = getCellType(row, col)

        if (kind === 'octagon') {
            // 8 neighbors: cardinal octagons + diagonal connectors
            const offsets = [
                { dr: -2, dc: 0 }, { dr: -1, dc: 1 }, { dr: 0, dc: 2 }, { dr: 1, dc: 1 },
                { dr: 2, dc: 0 }, { dr: 1, dc: -1 }, { dr: 0, dc: -2 }, { dr: -1, dc: -1 },
            ]
            for (let i = 0; i < 8; i++) {
                const nr = row + offsets[i].dr
                const nc = col + offsets[i].dc
                if (nr < 0 || nr >= h || nc < 0 || nc >= w) continue
                const neighbor = grid[nr]?.[nc]
                if (!neighbor?.active) continue

                // Check wall on our side
                const oct = cell as OctileCell
                if (!this.isPassable(oct.walls[i])) continue

                // Check wall on neighbor side
                const nWall = this.getNeighborWallIndex(row, col, nr, nc)
                if (nWall < 0) continue
                if (!this.isPassable(neighbor.walls[nWall])) continue

                // Check blocked gates
                if (blockedGates?.has(`${row},${col}>${nr},${nc}`)) continue

                result.push({ row: nr, col: nc })
            }
        } else if (kind === 'connector') {
            // 4 neighbors: diagonal octagons
            const offsets = [
                { dr: -1, dc: -1 }, { dr: -1, dc: 1 },
                { dr: 1, dc: 1 }, { dr: 1, dc: -1 },
            ]
            for (let i = 0; i < 4; i++) {
                const nr = row + offsets[i].dr
                const nc = col + offsets[i].dc
                if (nr < 0 || nr >= h || nc < 0 || nc >= w) continue
                const neighbor = grid[nr]?.[nc]
                if (!neighbor?.active) continue

                const conn = cell as SquareConnector
                if (!this.isPassable(conn.walls[i])) continue

                const nWall = this.getNeighborWallIndex(row, col, nr, nc)
                if (nWall < 0) continue
                if (!this.isPassable(neighbor.walls[nWall])) continue

                if (blockedGates?.has(`${row},${col}>${nr},${nc}`)) continue

                result.push({ row: nr, col: nc })
            }
        }

        return result
    }

    private isPassable(state: string): boolean {
        return PASSABLE_WALLS.includes(state as any)
    }

    private getNeighborWallIndex(fromR: number, fromC: number, toR: number, toC: number): number {
        const toKind = getCellType(toR, toC)
        if (toKind === 'octagon') {
            return getOctWallToward(toR, toC, fromR, fromC)
        } else if (toKind === 'connector') {
            return getConnWallToward(toR, toC, fromR, fromC)
        }
        return -1
    }

    // ─── Rule Helpers ───────────────────────────────────────

    private findEntrance(grid: GridCell[][], h: number, w: number): GridPos | null {
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (cell?.active && cell.kind === 'octagon' && (cell as OctileCell).type === 'entrance') {
                    return { row: r, col: c }
                }
            }
        }
        return null
    }

    private findUnreachable(
        grid: GridCell[][], h: number, w: number,
        reachable: Map<string, number>
    ): GridPos[] {
        const result: GridPos[] = []
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (cell?.active && !reachable.has(`${r},${c}`)) {
                    result.push({ row: r, col: c })
                }
            }
        }
        return result
    }

    private checkEntranceSafeZone(
        grid: GridCell[][], h: number, w: number,
        entrance: GridPos
    ): GridPos[] {
        const nearby = this.bfs(grid, h, w, entrance)
        const unsafe: GridPos[] = []

        for (const [key, dist] of nearby) {
            if (dist === 0) continue  // skip entrance itself
            if (dist > CELL_LIMITS.entranceSafeDistance) continue

            const [r, c] = key.split(',').map(Number)
            const cell = grid[r]?.[c]
            if (!cell?.active) continue

            if (cell.kind === 'octagon') {
                const oct = cell as OctileCell
                if (oct.spawns.length > 0 || oct.traps.length > 0) {
                    unsafe.push({ row: r, col: c })
                }
            } else if (cell.kind === 'connector') {
                const conn = cell as SquareConnector
                if (conn.traps.length > 0) {
                    unsafe.push({ row: r, col: c })
                }
            }
        }

        return unsafe
    }

    private findConsecutiveTrapCells(grid: GridCell[][], h: number, w: number): GridPos[] {
        const hazardSet = new Set<string>(HAZARD_TYPES)
        const violators = new Set<string>()

        // For each hazard cell, DFS to find chains > maxConsecutiveTrapCells
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active || cell.kind !== 'octagon') continue
                if (!hazardSet.has((cell as OctileCell).type)) continue

                // Check if this hazard cell has a safe zone within range
                const nearby = this.bfs(grid, h, w, { row: r, col: c })
                let hasSafe = false
                for (const [key, dist] of nearby) {
                    if (dist > CELL_LIMITS.minSafeZoneDistance) continue
                    const [nr, nc] = key.split(',').map(Number)
                    const n = grid[nr]?.[nc]
                    if (n?.active && n.kind === 'octagon' && SAFE_TYPES.includes((n as OctileCell).type)) {
                        hasSafe = true
                        break
                    }
                }
                if (!hasSafe) {
                    violators.add(`${r},${c}`)
                }
            }
        }

        return [...violators].map(k => {
            const [r, c] = k.split(',').map(Number)
            return { row: r, col: c }
        })
    }

    private findCellsByType(
        grid: GridCell[][], h: number, w: number,
        types: readonly CellType[]
    ): GridPos[] {
        const result: GridPos[] = []
        const typeSet = new Set<string>(types)
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (cell?.active && cell.kind === 'octagon' && typeSet.has((cell as OctileCell).type)) {
                    result.push({ row: r, col: c })
                }
            }
        }
        return result
    }

    private checkGatePrerequisites(
        grid: GridCell[][], h: number, w: number,
        entrance: GridPos | null
    ): Violation[] {
        if (!entrance) return []

        const violations: Violation[] = []

        // Find all GATE walls
        for (let r = 0; r < h; r++) {
            for (let c = 0; c < w; c++) {
                const cell = grid[r]?.[c]
                if (!cell?.active) continue

                const walls = cell.walls
                for (let i = 0; i < walls.length; i++) {
                    if (walls[i] !== 'GATE') continue

                    // Build blocked edge set for this gate
                    const blocked = new Set<string>()
                    const offsets = this.getNeighborOffset(r, c, i)
                    if (!offsets) continue
                    const { nr, nc } = offsets
                    blocked.add(`${r},${c}>${nr},${nc}`)
                    blocked.add(`${nr},${nc}>${r},${c}`)

                    // BFS from entrance without crossing this gate
                    const reachable = this.bfsAvoidingGate(grid, h, w, entrance, blocked)

                    // Check if any safe zone is reachable before the gate
                    let hasSafe = false
                    for (const key of reachable) {
                        const [sr, sc] = key.split(',').map(Number)
                        const s = grid[sr]?.[sc]
                        if (s?.active && s.kind === 'octagon') {
                            const type = (s as OctileCell).type
                            if (type === 'shrine' || type === 'alcove') {
                                hasSafe = true
                                break
                            }
                        }
                    }

                    if (!hasSafe) {
                        violations.push({
                            ruleId: 'gate-prerequisites',
                            severity: 'info',
                            message: `Gate at [${r},${c}] wall ${i} has no shrine/alcove reachable before it`,
                            cells: [{ row: r, col: c }],
                        })
                    }
                }
            }
        }

        return violations
    }

    /** Get neighbor position for a given cell's wall index */
    private getNeighborOffset(r: number, c: number, wallIdx: number): { nr: number; nc: number } | null {
        const kind = getCellType(r, c)
        if (kind === 'octagon') {
            const offsets = [
                { dr: -2, dc: 0 }, { dr: -1, dc: 1 }, { dr: 0, dc: 2 }, { dr: 1, dc: 1 },
                { dr: 2, dc: 0 }, { dr: 1, dc: -1 }, { dr: 0, dc: -2 }, { dr: -1, dc: -1 },
            ]
            if (wallIdx >= 0 && wallIdx < 8) {
                return { nr: r + offsets[wallIdx].dr, nc: c + offsets[wallIdx].dc }
            }
        } else if (kind === 'connector') {
            const offsets = [
                { dr: -1, dc: -1 }, { dr: -1, dc: 1 },
                { dr: 1, dc: 1 }, { dr: 1, dc: -1 },
            ]
            if (wallIdx >= 0 && wallIdx < 4) {
                return { nr: r + offsets[wallIdx].dr, nc: c + offsets[wallIdx].dc }
            }
        }
        return null
    }
}